/**
 * Notification action-URL handling.
 *
 * `notifications.action_url` is written at CREATION time, so stored rows
 * outlive route refactors. The emitters are fixed at the source in hermes
 * (2026-08), but prod still holds rows with pre-refactor URLs — normalize at
 * click time so history keeps working without a data migration.
 *
 * Navigation must also go through {@link parseActionUrl}: TanStack's
 * `navigate({ to: url })` does NOT parse a `#hash` (only the `href` option
 * does), so a comment anchor passed inside the `to` string is silently
 * dropped and the router's own hash scrolling never engages.
 */

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/** Rewrite stored legacy action URLs to their current-route equivalents. */
export function normalizeNotificationUrl(url: string): string {
  // Pre event-detail-route reminders pointed `?event=<id>` at the events
  // LIST, whose search schema has no such param (silently stripped — the
  // user landed on the generic list). The detail route is the target.
  const eventMatch = url.match(new RegExp(`^/events\\?event=(${UUID})$`));
  if (eventMatch) return `/events/${eventMatch[1]}`;

  // Lead notifications pointed `?lead=<id>` at the community route, whose
  // search schema has no such param. The lead's conversation id is not
  // recoverable client-side — land on the chats tab, where the lead thread
  // lives. (New lead notifications deep-link the conversation directly.)
  if (new RegExp(`^/community\\?lead=${UUID}$`).test(url)) {
    return '/community?tab=chats';
  }

  return url;
}

export interface ParsedActionUrl {
  to: string;
  search: Record<string, string>;
  hash: string | undefined;
}

/** Split an action URL into the pieces TanStack navigate() actually honours. */
export function parseActionUrl(url: string): ParsedActionUrl {
  const u = new URL(normalizeNotificationUrl(url), 'http://internal');
  const search: Record<string, string> = {};
  u.searchParams.forEach((value, key) => {
    search[key] = value;
  });
  return {
    to: u.pathname,
    search,
    hash: u.hash ? u.hash.slice(1) : undefined,
  };
}
